import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Modal, Spinner } from "react-bootstrap";
import {
  AlertCircle,
  AlertTriangle,
  Copy,
  Link,
  Lock,
  RefreshCw,
  Trash2,
  UserCheck,
  UserX,
  Users,
  XCircle,
} from "react-feather";
import axiosapi from "../../../utilities/axiosApi";

/**
 * Share link for a client event's manual-fulfilment campaigns.
 *
 * Read-and-manage view for staff: copy the URL and the 6-digit code to hand to
 * the customer, roll either one, block a single helper, or revoke the link.
 *
 * `basePath` selects the guard-prefixed routes; the API is identical under
 * /api/admin, /api/reseller and /api/customer.
 */
export default function ShareLinkPanel({ clientEventId, basePath }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  // The action the confirm dialog is currently asking about.
  const [pending, setPending] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchLink = useCallback(async () => {
    if (!clientEventId) {
      setLoading(false);
      return;
    }

    try {
      const res = await axiosapi.get(`${basePath}/share-link/${clientEventId}`);
      if (res.data?.success) {
        setLink(res.data.data);
        setError("");
      } else {
        setError(res.data?.message || "Could not load the share link.");
      }
    } catch (e) {
      setError("Could not load the share link.");
    } finally {
      setLoading(false);
    }
  }, [clientEventId, basePath]);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  const act = async (path, key, confirmText) => {
    if (confirmText) {
      setPending({ path, key, ...confirmText });
      return;
    }
    await run(path, key);
  };

  const run = async (path, key) => {
    setPending(null);

    setBusy(key);
    try {
      const res = await axiosapi.post(`${basePath}/share-link/${clientEventId}/${path}`);
      if (res.data?.success) {
        await fetchLink();
      } else {
        setError(res.data?.message || "That didn't work.");
      }
    } catch (e) {
      setError("That didn't work.");
    } finally {
      setBusy("");
    }
  };

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      // Clipboard needs a secure context; never leave staff with no way to copy.
      window.prompt("Copy this:", value);
    }
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Body className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  const revoked = link && (!link.is_active || link.revoked_at);

  return (
    <Card className="mb-3">
      <Card.Body>
        <h6 className="fw-bold mb-1">Helper share link</h6>
        <p className="text-muted small mb-3">
          {revoked
            ? "Revoked. Generate a new URL to share again."
            : "The customer sends this to anyone helping invite guests. No login needed."}
        </p>

        {error && (
          <p className="text-danger small d-flex align-items-center gap-2">
            <AlertCircle size={16} /> {error}
          </p>
        )}

        {link && !revoked && (
          <>
            <div className="mb-2">
              <label className="small fw-bold d-flex align-items-center gap-1">
                <Link size={16} /> Link
              </label>
              <div className="d-flex align-items-center gap-2">
                <code className="flex-grow-1 text-truncate bg-light p-2 rounded" title={link.url}>
                  {link.url}
                </code>
                <Button
                  size="sm"
                  variant="primary"
                  className="d-inline-flex align-items-center gap-1"
                  onClick={() => copy(link.url, "url")}>
                  <Copy size={16} />
                  {copied === "url" ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="mb-3">
              <label className="small fw-bold d-flex align-items-center gap-1">
                <Lock size={16} /> Access code
              </label>
              <div className="d-flex align-items-center gap-2">
                <code className="bg-light p-2 rounded fs-6">{link.otp}</code>
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="d-inline-flex align-items-center gap-1"
                  onClick={() => copy(link.otp, "otp")}>
                  <Copy size={16} />
                  {copied === "otp" ? "Copied" : "Copy"}
                </Button>
              </div>
              <small className="text-muted">
                Fixed code. Anyone with the link and this code can see the event's guest list.
              </small>
            </div>

            {(() => {
              const devices = link.devices || [];
              const active = devices.filter((d) => !d.is_stale);
              const inactive = devices.filter((d) => d.is_stale);
              const slotsFull = link.active_device_count >= link.max_devices;

              const row = (d) => {
                const key = `dev-${d.id}`;
                const working = busy === key;
                return (
                  <div
                    key={d.id}
                    className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
                  >
                    <div className="min-w-0 flex-grow-1">
                      <div className={`text-truncate ${d.is_blocked ? "text-muted" : "fw-semibold"}`}>
                        {d.name}
                        {d.is_blocked && (
                          <span className="badge rounded-pill bg-secondary fw-normal ms-2 align-middle">
                            Blocked
                          </span>
                        )}
                      </div>
                      <small className="text-muted d-flex flex-wrap gap-2">
                        <span>{d.device_label}</span>
                        <span>{d.last_seen_at ? `active ${timeAgo(d.last_seen_at)}` : "not yet active"}</span>
                        {d.sent_count > 0 && <span>{d.sent_count} sent</span>}
                        {d.called_count > 0 && <span>{d.called_count} called</span>}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={working}
                        onClick={() => act(`devices/${d.id}/block`, key)}
                        className="d-inline-flex align-items-center gap-1"
                      >
                        {d.is_blocked ? <UserCheck size={16} /> : <UserX size={16} />}
                        {d.is_blocked ? "Unblock" : "Block"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={working}
                        aria-label={`Remove ${d.name}`}
                        title="Remove"
                        onClick={() =>
                          act(`devices/${d.id}/remove`, key, {
                            title: `Remove ${d.name}?`,
                            body: "This device is signed out and leaves the list. Anything they already marked stays recorded under their name, and they can join again with the link and code if there is room.",
                            confirmLabel: "Remove helper",
                          })
                        }
                        className="d-inline-flex align-items-center"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                );
              };

              return (
                <div className="mb-3">
                  <label className="small fw-bold d-flex align-items-center gap-1 mb-1">
                    <Users size={16} /> Helpers
                    <span className="text-muted fw-normal ms-1">
                      {link.active_device_count} of {link.max_devices} slots used
                    </span>
                  </label>
                  {slotsFull && (
                    <p className="small text-warning-emphasis d-flex align-items-center gap-2 mb-2">
                      <AlertTriangle size={16} /> Every slot is in use. Remove or block a helper before
                      someone new can join.
                    </p>
                  )}
                  {devices.length === 0 ? (
                    <small className="text-muted d-block">
                      No one has opened the link yet. Helpers appear here as they join.
                    </small>
                  ) : (
                    active.map(row)
                  )}
                  {inactive.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mt-2 text-muted text-decoration-none"
                        onClick={() => setShowInactive((v) => !v)}
                      >
                        {showInactive ? "Hide" : "Show"} {inactive.length} inactive (not seen in 30 days)
                      </button>
                      {showInactive && <div className="opacity-75">{inactive.map(row)}</div>}
                    </>
                  )}
                </div>
              );
            })()}
          </>
        )}

        <div className="d-flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-1"
            disabled={!!busy}
            onClick={() =>
              act("rotate-otp", "otp", {
                title: "Generate a new access code?",
                body: "The current code stops working immediately, and every helper has to be given the new one.",
                confirmLabel: "Generate new code",
              })
            }
          >
            {busy === "otp" ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
              <RefreshCw size={16} />
            )}
            New code
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-1"
            disabled={!!busy}
            onClick={() =>
              act("rotate-token", "token", {
                title: "Generate a new URL?",
                body: "The current link stops working immediately. Anyone already using it is signed out and needs the new URL.",
                confirmLabel: "Generate new URL",
              })
            }
          >
            {busy === "token" ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
              <RefreshCw size={16} />
            )}
            New URL
          </Button>
          {!revoked && (
            <Button
              size="sm"
              variant="outline-danger"
              className="d-inline-flex align-items-center gap-1 ms-auto"
              disabled={!!busy}
              onClick={() =>
                act("revoke", "revoke", {
                  title: "Revoke this link?",
                  body: "Everyone currently using it is signed out straight away. A new URL can be generated afterwards.",
                  confirmLabel: "Revoke link",
                })
              }
            >
              {busy === "revoke" ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
              <XCircle size={16} />
            )}
            Revoke
            </Button>
          )}
        </div>
      </Card.Body>

      <Modal show={!!pending} onHide={() => setPending(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{pending?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">{pending?.body}</Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="outline-secondary" onClick={() => setPending(null)}>
            Keep it
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={!!busy}
            onClick={() => run(pending.path, pending.key)}
          >
            {pending?.confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

/** "just now", "5 min ago", "3 hours ago", "yesterday", "12 days ago". */
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "yesterday";
  return `${d} days ago`;
}
